import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { fetchAuth } from '../../utils/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function ProfitReportView({ user, handleApiError, showNotification }) {
    const [profitData, setProfitData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfitData = async () => {
            try {
                const res = await fetchAuth(`${API_URL}/api/profit/monthly`);
                if (res.ok) {
                    setProfitData(await res.json());
                } else {
                    throw new Error('Failed to fetch profit data.');
                }
            } catch (error) {
                handleApiError(error);
            } finally {
                setLoading(false);
            }
        };
        fetchProfitData();
    }, [handleApiError]);

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
            <p>Loading profit report...</p>
        </div>;
    }

    if (!user || user.role !== 'Admin') {
        return <p>Access Denied: Only administrators can view this report.</p>;
    }

    const formatCurrency = (val) => `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Summary totals
    const totalSalesAll = profitData.reduce((sum, d) => sum + Number(d.totalSales || 0), 0);
    const totalPurchasesAll = profitData.reduce((sum, d) => sum + Number(d.totalPurchases || 0), 0);
    const totalExpensesAll = profitData.reduce((sum, d) => sum + Number(d.totalExpenses || 0), 0);
    const totalPureProfitAll = profitData.reduce((sum, d) => sum + Number(d.pureProfit || 0), 0);

    const chartConfig = {
        labels: profitData.map(data => data.month).reverse(),
        datasets: [
            {
                label: 'Total Sales (₹)',
                data: profitData.map(data => data.totalSales).reverse(),
                backgroundColor: 'rgba(99, 102, 241, 0.8)',
                borderRadius: 6,
            },
            {
                label: 'Total Purchases (₹)',
                data: profitData.map(data => data.totalPurchases).reverse(),
                backgroundColor: 'rgba(239, 68, 68, 0.7)',
                borderRadius: 6,
            },
            {
                label: 'Expenses (₹)',
                data: profitData.map(data => data.totalExpenses || 0).reverse(),
                backgroundColor: 'rgba(245, 158, 11, 0.7)',
                borderRadius: 6,
            },
            {
                label: 'Pure Profit (₹)',
                data: profitData.map(data => data.pureProfit).reverse(),
                backgroundColor: 'rgba(34, 197, 94, 0.8)',
                borderRadius: 6,
            }
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Monthly Profit Report (Sales − Purchases − Expenses)', font: { size: 14 } },
        },
        scales: {
            x: { stacked: false, grid: { display: false } },
            y: { stacked: false, beginAtZero: true },
        },
    };

    const cardStyle = (color) => ({
        background: `linear-gradient(135deg, ${color}15, ${color}08)`,
        border: `1px solid ${color}30`,
        borderRadius: '12px', padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '6px'
    });
    const labelStyle = { fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' };
    const valueStyle = (color) => ({ fontSize: '22px', fontWeight: 700, color });

    return (
        <div className="page-container">
            <div className="page-header">
                <h2 className="page-title">Monthly Profit Report</h2>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={cardStyle('#6366f1')}>
                    <span style={labelStyle}>Total Sales</span>
                    <span style={valueStyle('#6366f1')}>{formatCurrency(totalSalesAll)}</span>
                </div>
                <div style={cardStyle('#ef4444')}>
                    <span style={labelStyle}>Total Purchases</span>
                    <span style={valueStyle('#ef4444')}>{formatCurrency(totalPurchasesAll)}</span>
                </div>
                <div style={cardStyle('#f59e0b')}>
                    <span style={labelStyle}>Total Expenses</span>
                    <span style={valueStyle('#f59e0b')}>{formatCurrency(totalExpensesAll)}</span>
                </div>
                <div style={cardStyle(totalPureProfitAll >= 0 ? '#22c55e' : '#ef4444')}>
                    <span style={labelStyle}>Pure Profit</span>
                    <span style={valueStyle(totalPureProfitAll >= 0 ? '#22c55e' : '#ef4444')}>{formatCurrency(totalPureProfitAll)}</span>
                </div>
            </div>

            {/* Chart */}
            <div className="data-display" style={{ marginBottom: '24px' }}>
                <div style={{ height: '400px' }}>
                    <Bar data={chartConfig} options={chartOptions} />
                </div>
            </div>

            {/* Breakdown Table */}
            <div className="data-display">
                <h3>Profit Breakdown</h3>
                <div className="inventory-table-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Month</th>
                                <th>Total Sales</th>
                                <th>Total Purchases</th>
                                <th>Expenses</th>
                                <th>Pure Profit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {profitData.map(item => {
                                const profit = Number(item.pureProfit || 0);
                                return (
                                    <tr key={item.month}>
                                        <td>{item.month}</td>
                                        <td>{formatCurrency(item.totalSales)}</td>
                                        <td>{formatCurrency(item.totalPurchases)}</td>
                                        <td style={{ color: '#f59e0b', fontWeight: 600 }}>{formatCurrency(item.totalExpenses)}</td>
                                        <td style={{ color: profit >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
                                            {formatCurrency(profit)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default ProfitReportView;
